import { z } from "zod";
import { generatedCardsSchema } from "./transcript.js";
import { limits } from "./limits.js";

/**
 * The Edit Decision List: what the AI decided, before anything acts on it.
 *
 * This exists to draw a line. The model reasons about *what the video should
 * be* — which moments to show, what to say over them, what to drop — and stops
 * there. Everything downstream is deterministic: ffmpeg cuts nothing it was not
 * told to, ElevenLabs speaks the text it is given, Remotion renders a timeline
 * that was computed in code. A model that could reach past this boundary would
 * make renders irreproducible and bugs unfindable.
 *
 * So an EDL is a *proposal*, and every field is treated as such. It is
 * validated on arrival, checked against the recording it claims to describe,
 * and only then converted into a timeline.
 *
 * `confidence` and `reason` are not decoration. They are how a decision can be
 * reviewed later — by a person reading the job log, or by code that declines to
 * act on something the model was unsure of.
 */

export const edlActions = ["show", "cut", "optional"] as const;
export type EdlAction = (typeof edlActions)[number];

/** A moment in the *source* recording. Never in finished-timeline time. */
export const sourceRangeSchema = z
  .object({
    start: z.number().nonnegative(),
    end: z.number().nonnegative(),
  })
  .refine((range) => range.end > range.start, "a range must end after it starts");

export const edlSceneSchema = z.object({
  id: z.string().min(1).max(64),
  /** What the narrator says over this stretch. Empty is allowed for a silent beat. */
  narration: z.string().max(4000).default(""),
  source: sourceRangeSchema,
  /**
   * `show` keeps it, `cut` removes it, `optional` keeps it but marks it as the
   * first thing to drop if the video needs to be shorter. Nothing acts on
   * `optional` yet; it is recorded so a later pass can.
   */
  action: z.enum(edlActions).default("show"),
  /** Why this scene exists, in the model's words. For the job log and the editor. */
  reason: z.string().max(400).default(""),
  confidence: z.number().min(0).max(1).default(1),
});

/**
 * A stretch of the recording that could be removed without touching anything.
 *
 * Worked out by arithmetic from the recording's own timings, never proposed by
 * a model — which is the point. A model asked for free-form ranges named the
 * wrong side of every boundary on a real recording; asked to pick from these,
 * it cannot name a range that contains speech at all.
 */
export interface CutCandidate {
  /** Stable within one planning call, so a model can name it back. */
  id: string;
  start: number;
  end: number;
  duration: number;
}

export const edlCutSchema = z.object({
  start: z.number().nonnegative(),
  end: z.number().nonnegative(),
  reason: z.string().max(400).default(""),
  confidence: z.number().min(0).max(1).default(1),
});

/**
 * A caption burned over the video at a moment, independent of narration.
 *
 * Separate from the caption track, which follows the spoken words. These are
 * the editor's own annotations — "Create your first project" over the moment
 * it happens — and they belong to the edit rather than to the transcript.
 */
export const edlOverlaySchema = z.object({
  text: z.string().min(1).max(200),
  /** Seconds into the *source* recording, like everything else in an EDL. */
  start: z.number().nonnegative(),
  duration: z.number().positive().max(60),
});

export const edlSchema = z.object({
  /** Bumped when the shape changes, so a stored EDL can be read by later code. */
  version: z.literal("1.0").default("1.0"),
  scenes: z.array(edlSceneSchema).max(limits.maxScenes).default([]),
  cuts: z.array(edlCutSchema).max(limits.maxScenes).default([]),
  overlays: z.array(edlOverlaySchema).max(200).default([]),
  /**
   * Words for the HEAD and END cards. Optional in every part: a recording that
   * does not say enough to title it honestly should leave the card empty rather
   * than fill it with a confident guess.
   */
  cards: generatedCardsSchema.optional(),
  /** What the call cost, when the provider says. */
  usage: z
    .object({
      model: z.string().max(80).optional(),
      inputTokens: z.number().int().nonnegative().optional(),
      outputTokens: z.number().int().nonnegative().optional(),
    })
    .optional(),
});

export type SourceRange = z.infer<typeof sourceRangeSchema>;
export type EdlScene = z.infer<typeof edlSceneSchema>;
export type EdlCut = z.infer<typeof edlCutSchema>;
export type EdlOverlay = z.infer<typeof edlOverlaySchema>;
export type Edl = z.infer<typeof edlSchema>;

/**
 * A frame handed to the editor as visual context.
 *
 * Nothing produces these yet, and nothing requires them. The type exists so the
 * editor interface can accept visual context the day a vision model is worth
 * the call, without that being a change to every signature between here and the
 * pipeline.
 */
export interface Keyframe {
  timestamp: number;
  imagePath: string;
  source: "scene_boundary" | "action" | "interval";
}

/**
 * The old scene shape, as stored before the EDL existed.
 *
 * Kept as its own schema rather than folded into the new one: the two are
 * genuinely different documents, and pretending otherwise is how a migration
 * quietly starts accepting malformed new data because it looks like valid old
 * data.
 */
export const legacySceneSchema = z.object({
  id: z.string().min(1).max(64),
  script: z.string().max(4000).default(""),
  sourceStart: z.number().nonnegative(),
  sourceEnd: z.number().nonnegative(),
});

export type LegacyScene = z.infer<typeof legacySceneSchema>;

/**
 * Reads scenes written before the EDL into the current shape.
 *
 * Every project stored before this change has the old form, and re-running the
 * model over all of them would cost money and produce different videos — so the
 * old form is translated rather than replaced. What is missing is filled in
 * honestly: these scenes were all kept, so `show`; nothing recorded why, so no
 * reason; and confidence is 1 because a human has been looking at this script
 * and has not objected, which is better evidence than a model's own estimate.
 */
export function edlFromLegacyScenes(scenes: readonly LegacyScene[]): Edl {
  return edlSchema.parse({
    version: "1.0",
    scenes: scenes
      .filter((scene) => scene.sourceEnd > scene.sourceStart)
      .map((scene) => ({
        id: scene.id,
        narration: scene.script,
        source: { start: scene.sourceStart, end: scene.sourceEnd },
        action: "show" as const,
        reason: "",
        confidence: 1,
      })),
    cuts: [],
    overlays: [],
  });
}

/**
 * The scenes a timeline should be built from: those meant to be shown.
 *
 * `optional` counts as shown. It marks a candidate for a future shortening
 * pass, not something to drop on sight — silently omitting content the model
 * merely flagged as skippable would lose steps nobody agreed to lose.
 */
export function shownScenes(edl: Edl): EdlScene[] {
  return edl.scenes.filter((scene) => scene.action !== "cut").sort((a, b) => a.source.start - b.source.start);
}
